import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  useTicketArticleLinks,
  useLinkArticleToTicket,
  useUnlinkArticleFromTicket,
  useSuggestArticlesForTicket,
} from "../../hooks/useTicketArticleLinks";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";
import {
  BookOpen,
  Plus,
  X,
  Loader2,
  Search,
  CheckCircle2,
  Link2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  ArticleLinkType,
  ArticleLinkTypeLabels,
  ArticleLinkTypeColors,
  ArticleStatus,
  ArticleStatusColors,
  ArticleVisibility,
  ArticleVisibilityColors,
} from "../../types/articles";
import type { TicketArticleLinkResponse, ArticleDto } from "../../types/articles";
import { useHelpdeskNavigation } from "../../hooks/useHelpdeskNavigation";
import { useDebounce } from "../../hooks/useDebounce";

interface TicketArticleLinksProps {
  ticketId: string;
  organizationId?: string;
  embedded?: boolean;
  readOnly?: boolean; // Hides add/remove controls - merged tickets are read-only
}

export function TicketArticleLinks({ ticketId, organizationId, embedded = false, readOnly = false }: TicketArticleLinksProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLinkType, setSelectedLinkType] = useState<ArticleLinkType>(ArticleLinkType.Reference);
  const { navigateTo } = useHelpdeskNavigation();

  const { data: articleLinksData, isLoading } = useTicketArticleLinks(ticketId, organizationId);
  // Searched as the agent pauses typing, not on every keystroke; the last results stay up meanwhile
  const debouncedSearch = useDebounce(searchQuery.trim(), 300);
  const { data: suggestedArticles = [], isLoading: isSearching } = useSuggestArticlesForTicket(
    ticketId,
    debouncedSearch,
    organizationId,
    isAddDialogOpen
  );
  const linkArticleMutation = useLinkArticleToTicket();
  const unlinkArticleMutation = useUnlinkArticleFromTicket();

  const links = articleLinksData?.links ?? [];
  const linkedArticleIds = new Set(links.map(l => l.articleId));

  const handleLinkArticle = async (article: ArticleDto) => {
    try {
      await linkArticleMutation.mutateAsync({
        ticketId,
        request: {
          articleId: article.id,
          linkType: selectedLinkType,
        },
        organizationId,
      });
      showToast.success("Article Linked", `"${article.title}" has been linked to this ticket`);
    } catch (error) {
      showToast.error("Error", getErrorMessage(error, "Failed to link article"));
    }
  };

  const handleUnlinkArticle = async (link: TicketArticleLinkResponse) => {
    try {
      await unlinkArticleMutation.mutateAsync({
        ticketId,
        articleId: link.articleId,
        organizationId,
      });
      showToast.success("Article Unlinked", `"${link.articleTitle}" has been removed`);
    } catch (error) {
      showToast.error("Error", getErrorMessage(error, "Failed to unlink article"));
    }
  };

  const content = (
    <>
      <div className="flex items-center justify-between mb-3">
        <h4 className={embedded ? "text-sm font-medium text-muted-foreground flex items-center gap-2" : "text-lg font-semibold text-foreground flex items-center gap-2"}>
          <BookOpen className="h-4 w-4" />
          Linked Articles
          {links.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">
              ({links.length})
            </span>
          )}
        </h4>
        {!readOnly && (
          <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
            setIsAddDialogOpen(open);
            if (!open) {
              setSearchQuery("");
              setSelectedLinkType(ArticleLinkType.Reference);
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" aria-label="Link an article">
                <Plus className="h-3 w-3" />
                <span className="hidden sm:inline">Link</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Link Article</DialogTitle>
                <DialogDescription>
                  Search for knowledge base articles to link to this ticket.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-foreground whitespace-nowrap">Link as:</label>
                  <select
                    value={selectedLinkType}
                    onChange={(e) => setSelectedLinkType(parseInt(e.target.value) as ArticleLinkType)}
                    className="flex-1 px-3 py-1.5 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={ArticleLinkType.Reference}>Reference</option>
                    <option value={ArticleLinkType.Solution}>Solution</option>
                  </select>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search articles..."
                    aria-label="Search articles to link"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    autoFocus
                  />
                </div>
                {/* Nothing typed: the API suggests articles related to the ticket's subject and description */}
                <p className="text-xs font-medium text-muted-foreground">
                  {debouncedSearch ? "Matching articles" : "Suggested for this ticket"}
                </p>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {isSearching ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : suggestedArticles.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No matching articles found
                    </p>
                  ) : (
                    suggestedArticles.map((article) => {
                      const isAlreadyLinked = linkedArticleIds.has(article.id);

                      return (
                        <button
                          key={article.id}
                          onClick={() => !isAlreadyLinked && handleLinkArticle(article)}
                          disabled={isAlreadyLinked || linkArticleMutation.isPending}
                          className={`w-full flex items-start gap-3 p-2.5 rounded-lg transition-colors text-left ${
                            isAlreadyLinked
                              ? "opacity-50 cursor-not-allowed bg-muted/50"
                              : "hover:bg-muted/50 cursor-pointer"
                          }`}
                        >
                          <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            {isAlreadyLinked ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <BookOpen className="h-3.5 w-3.5 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {article.title}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {(article.collectionPath || article.collectionName) && (
                                <span className="text-xs text-muted-foreground">{article.collectionPath || article.collectionName}</span>
                              )}
                              {article.visibility === ArticleVisibility.Internal && (
                                <Badge className={`text-[10px] px-1 py-0 ${ArticleVisibilityColors[ArticleVisibility.Internal]}`}>
                                  Internal
                                </Badge>
                              )}
                            </div>
                            {article.summary && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {article.summary}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : links.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3">
          No articles linked
        </p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {links.map((link) => (
            <div
              key={link.id}
              className="flex items-start gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors group"
            >
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                {link.linkType === ArticleLinkType.Solution ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <Link2 className="h-4 w-4 text-blue-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => navigateTo.article(link.articleId)}
                  className="text-sm font-medium text-foreground hover:text-primary hover:underline truncate block text-left"
                >
                  {link.articleTitle}
                </button>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Badge className={`text-[10px] px-1 py-0 ${ArticleLinkTypeColors[link.linkType as ArticleLinkType]}`}>
                    {ArticleLinkTypeLabels[link.linkType as ArticleLinkType]}
                  </Badge>
                  {link.articleVisibility === ArticleVisibility.Internal && (
                    <Badge className={`text-[10px] px-1 py-0 ${ArticleVisibilityColors[ArticleVisibility.Internal]}`}>
                      Internal
                    </Badge>
                  )}
                  {link.articleStatus === ArticleStatus.Archived && (
                    <Badge className={`text-[10px] px-1 py-0 ${ArticleStatusColors[ArticleStatus.Archived]}`}>
                      Archived
                    </Badge>
                  )}
                </div>
              </div>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleUnlinkArticle(link)}
                  disabled={unlinkArticleMutation.isPending}
                  aria-label={`Unlink ${link.articleTitle}`}
                  title="Unlink"
                  // Shown on hover with a mouse; always visible on touch screens (no hover) and when focused
                  className="h-6 w-6 flex-shrink-0 transition-opacity [@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                >
                  {unlinkArticleMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="p-3 sm:p-4 rounded-lg border border-border bg-card shadow-sm">
      {content}
    </div>
  );
}
